// Drag and drop for any list a person orders by hand (ORDER_DRAG_ROOM_CODE_B3_WORDS.md, approved 2026-10-28 6:35 AM).
// Field Labels Order uses it first; the Lists page is next (HANDOFF_BUILD_B3.md item 2a).
//
// The page marks each thing that moves with data-drag-item="<id>" and puts a handle inside it. Things that move
// together sit in one parent element: a panel's block among the room's blocks, a subpanel among its parent's.
// Press and hold the handle; the item lifts and follows the finger among its own peers; lift to drop. The piece
// only reports where it landed. The page decides what that means (names, saving) and redraws.

/** How long the handle is held before the item lifts, so a quick swipe never drags (iPhone lists work this way). */
export const HOLD_MS = 180;
/** How close to the list's top or bottom edge, in pixels, before the list scrolls on its own. */
const EDGE = 48;
const EDGE_STEP = 14;

/** The place the item lands: above the first peer whose middle sits below the finger, else last. */
export function dropIndex(peerMiddles: readonly number[], y: number): number {
  const i = peerMiddles.findIndex((m) => y < m);
  return i < 0 ? peerMiddles.length : i;
}

let lifted = 0;
/** True while an item is held up. A page that repaints itself on its own (an Outbox save) waits until the drop. */
export const dragActive = (): boolean => lifted > 0;

export interface DragOrderOptions {
  /** The handle inside each item. Only a press here starts a drag; the rest of the card scrolls as usual. */
  handle: string;
  /** The element that scrolls the list. It scrolls when the item is dragged to its top or bottom edge. */
  scroller: () => HTMLElement | null;
  /** Called once on lift with the item's id, its place among its peers before, and the place it landed. */
  onDrop: (id: string, from: number, to: number) => void;
}

/** Starts listening on `box` (the list). One call per page; the list itself may be redrawn freely. */
export function dragOrder(box: HTMLElement, o: DragOrderOptions): void {
  let d: {
    item: HTMLElement;
    handle: HTMLElement;
    pointer: number;
    y0: number;
    grab: number;
    from: number;
    live: boolean;
    timer: number;
    lastY: number;
    raf: number;
  } | null = null;

  const peersOf = (item: HTMLElement): HTMLElement[] =>
    [...(item.parentElement?.children ?? [])].filter((x): x is HTMLElement => x instanceof HTMLElement && x.hasAttribute("data-drag-item"));

  const place = (y: number): void => {
    if (!d) return;
    const { item } = d;
    const others = peersOf(item).filter((p) => p !== item);
    const at = dropIndex(
      others.map((p) => {
        const r = p.getBoundingClientRect();
        return r.top + r.height / 2;
      }),
      y,
    );
    const parent = item.parentElement!;
    const before = others[at] ?? null;
    if (before) {
      if (item.nextElementSibling !== before) parent.insertBefore(item, before);
    } else if (others.length) {
      const last = others[others.length - 1]!;
      if (last.nextElementSibling !== item) last.after(item);
    }
    item.style.transform = "";
    const natural = item.getBoundingClientRect().top;
    item.style.transform = `translateY(${y - d.grab - natural}px)`;
  };

  // While the finger rests near an edge, the list keeps scrolling.
  const edgeScroll = (): void => {
    if (!d?.live) return;
    const sc = o.scroller();
    if (sc) {
      const r = sc.getBoundingClientRect();
      const step = d.lastY < r.top + EDGE ? -EDGE_STEP : d.lastY > r.bottom - EDGE ? EDGE_STEP : 0;
      if (step) {
        sc.scrollTop += step;
        place(d.lastY);
      }
    }
    d.raf = requestAnimationFrame(edgeScroll);
  };

  const finish = (e: PointerEvent): void => {
    if (!d || e.pointerId !== d.pointer) return;
    const { item, handle, from, live, timer, raf } = d;
    window.clearTimeout(timer);
    cancelAnimationFrame(raf);
    handle.classList.remove("hold");
    item.classList.remove("lifted");
    item.style.transform = "";
    d = null;
    if (!live) return;
    lifted = Math.max(0, lifted - 1);
    const to = peersOf(item).indexOf(item);
    o.onDrop(item.dataset.dragItem ?? "", from, to);
  };

  box.addEventListener("pointerdown", (e) => {
    const handle = (e.target as HTMLElement).closest<HTMLElement>(o.handle);
    const item = handle?.closest<HTMLElement>("[data-drag-item]");
    if (!handle || !item || !box.contains(item) || d) return;
    e.preventDefault();
    handle.setPointerCapture(e.pointerId);
    handle.classList.add("hold");
    d = { item, handle, pointer: e.pointerId, y0: e.clientY, grab: 0, from: peersOf(item).indexOf(item), live: false, timer: 0, lastY: e.clientY, raf: 0 };
    d.timer = window.setTimeout(() => {
      if (!d) return;
      d.live = true;
      lifted += 1;
      d.grab = d.y0 - item.getBoundingClientRect().top;
      item.classList.add("lifted");
      d.raf = requestAnimationFrame(edgeScroll);
    }, HOLD_MS);
  });
  box.addEventListener("pointermove", (e) => {
    if (!d || e.pointerId !== d.pointer) return;
    d.lastY = e.clientY;
    if (d.live) place(e.clientY);
  });
  box.addEventListener("pointerup", finish);
  box.addEventListener("pointercancel", finish);
}
